<template>
  <div class="summary-page-wrap">
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else-if="error" class="text-center py-5 text-danger">
      <i class="material-icons" style="font-size:48px;">error_outline</i>
      <p class="mt-2">{{ error }}</p>
    </div>
    <div v-else class="summary-page">
      <!-- Visual header -->
      <div class="sp-header" :class="headerClass">
        <i class="material-icons sp-header-icon">{{ headerIcon }}</i>
        <div>
          <h5 class="sp-header-title">{{ title }}</h5>
          <p class="sp-header-sub" v-if="subtitle">{{ subtitle }}</p>
        </div>
        <!-- Sign-up Summary button for signup-only AIWGs -->
        <button
          v-if="isSignupOnly"
          class="btn btn-sm btn-outline-light ms-auto"
          @click="showSignupSummary = true"
          title="View sign-up notification summary"
        >
          <i class="material-icons text-sm align-middle me-1">how_to_reg</i>
          Sign-up Notification Summary
        </button>
      </div>
      <SummarySectionMenu :sections="sectionMenuItems" />
      <div v-if="confidenceScore !== null" class="confidence-summary">
        <i class="material-icons">analytics</i>
        <span>Scheduling confidence</span>
        <strong>{{ confidenceScore }}%</strong>
      </div>
      <section v-if="confidenceDetails.length" class="scheduled-people" aria-labelledby="scheduled-people-title">
        <div class="scheduled-people-heading">
          <div>
            <h6 id="scheduled-people-title">Scheduled people</h6>
            <p>Review the criteria and scheduling rules behind each scheduling decision.</p>
          </div>
          <i class="material-icons" aria-hidden="true">people</i>
        </div>
        <div class="scheduled-person-list">
          <div
            v-for="person in confidenceDetails"
            :key="personConfidenceKey(person)"
            class="scheduled-person-row"
          >
            <div class="scheduled-person-identity">
              <i class="material-icons" aria-hidden="true">person</i>
              <div>
                <strong>{{ person.person_name }}</strong>
                <span v-if="person.person_id !== null">Person ID: {{ person.person_id }}</span>
              </div>
            </div>
            <div class="scheduled-person-actions">
              <span class="scheduled-person-score" :class="personScoreClass(person.score)">
                {{ formatConfidenceScore(person.score) }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary m-0"
                :aria-label="`View scheduling confidence details for ${person.person_name}`"
                @click="openConfidenceDetails(person)"
              >
                <i class="material-icons text-sm align-middle me-1" aria-hidden="true">analytics</i>
                Details
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Grouped summary items -->
      <div v-for="group in groupedItems" :id="group.id" :key="group.id" class="sp-category">
        <h6 class="sp-cat-title">
          <i class="material-icons">{{ categoryIcon(group.category) }}</i>
          {{ group.category }}
        </h6>
        <div v-for="(item, iIdx) in group.items" :key="iIdx" class="sp-item" :class="itemClass(item)">
          <i class="material-icons sp-item-icon" :class="iconColorClass(item)">{{ item.icon || 'info' }}</i>
          <div class="sp-item-text">
            <template v-if="item.rule_title">
              <strong>{{ item.rule_title }}</strong>
              <p><b>Configured:</b> {{ item.rule_configuration }}</p>
              <p><b>What this means:</b> {{ item.rule_explanation }}</p>
            </template>
            <template v-else-if="item.rule_notice">{{ item.rule_notice }}</template>
            <span v-else v-html="md(item.text)"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Sign-up Summary Modal -->
    <SignupSummaryModal
      v-if="isSignupOnly"
      v-model="showSignupSummary"
      :locationId="metaLocationId"
      :aiwgId="metaAiwgId"
    />

    <SchedulingConfidenceModal
      v-model="showConfidenceDetails"
      :confidence="selectedConfidence"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import SignupSummaryModal from '@/components/Modal/SignupSummaryModal.vue'
import SchedulingConfidenceModal from '@/components/Modal/SchedulingConfidenceModal.vue'
import SummarySectionMenu from '@/components/Modal/SummarySectionMenu.vue'

const route = useRoute()
const store = useStore()
const loading = ref(true)
const error = ref('')
const summaryList = ref([])
const title = ref('')
const subtitle = ref('')
const summaryType = ref('')
const isSignupOnly = ref(false)
const metaLocationId = ref(null)
const metaAiwgId = ref(null)
const showSignupSummary = ref(false)
const confidenceScore = ref(null)
const confidenceDetails = ref([])
const selectedConfidence = ref(null)
const showConfidenceDetails = ref(false)

const headerClass = computed(() => {
  if (summaryType.value === 'activity-instance') return 'sp-header-purple'
  if (summaryType.value === 'aiwg') return 'sp-header-teal'
  return 'sp-header-blue'
})

const headerIcon = computed(() => {
  if (summaryType.value === 'activity-instance') return 'event'
  if (summaryType.value === 'aiwg') return 'assignment'
  return 'summarize'
})

const groupedItems = computed(() => {
  const groups = {}
  for (const item of summaryList.value) {
    const cat = item.category || 'General'
    if (!groups[cat]) groups[cat] = { category: cat, items: [] }
    groups[cat].items.push(item)
  }
  return Object.values(groups).map((group, index) => ({
    ...group,
    id: `summary-section-${index}-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  }))
})

const sectionMenuItems = computed(() => groupedItems.value.map(({ id, category }) => ({
  id,
  label: category,
})))

const categoryIcon = (cat) => {
  const icons = {
    'General': 'info', 'Status': 'flag', 'Schedule': 'calendar_today', 'Scheduling': 'event_repeat',
    'Scheduling Mode': 'auto_fix_high', 'Sign-Up Mode': 'person_add',
    'Inherited from Activity': 'account_tree', 'Inherited from Workgroup': 'account_tree',
    'Workgroups': 'group_work', 'Workgroup Details': 'assignment', 'Volunteers': 'people',
    'Auto-Decline': 'timer_off', 'Teams': 'groups', 'Notes': 'note', 'Visibility': 'visibility',
    'Notifications': 'notifications',
    'Scheduling Rules': 'rule',
  }
  return icons[cat] || 'label'
}

const itemClass = (item) => {
  if (item.icon === 'warning') return 'sp-item-warn'
  if (item.icon === 'check_circle') return 'sp-item-ok'
  if (item.icon === 'cancel') return 'sp-item-err'
  return ''
}

const iconColorClass = (item) => {
  if (item.icon === 'warning') return 'sp-icon-warn'
  if (item.icon === 'check_circle') return 'sp-icon-ok'
  if (item.icon === 'cancel') return 'sp-icon-err'
  return ''
}

const md = (text) => {
  if (!text) return ''
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
}
const isConfidenceRecord = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return ['score', 'person_id', 'personId', 'criteria', 'scheduling_rules'].some((key) => key in value)
}

const asConfidenceRecords = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(isConfidenceRecord)
  if (isConfidenceRecord(value)) return [value]
  if (typeof value === 'object') return Object.values(value).filter(isConfidenceRecord)
  return []
}

const normalizeConfidenceRecord = (record, index) => {
  const person = record.person && typeof record.person === 'object' ? record.person : {}
  const personId = record.person_id ?? record.personId ?? person.id ?? null
  const personName = record.person_name
    || record.personName
    || record.display_name
    || record.name
    || person.name
    || person.display_name
    || record.email
    || (personId !== null ? `Person #${personId}` : `Scheduled person ${index + 1}`)

  return {
    ...record,
    person_id: personId,
    person_name: personName,
    score: record.score ?? record.confidence_score ?? record.scheduling_confidence_score ?? null,
    version: record.version ?? null,
    location_id: record.location_id ?? record.locationId ?? null,
    activity_instance_id: record.activity_instance_id ?? record.activityInstanceId ?? null,
    activity_instance_workgroup_id: record.activity_instance_workgroup_id
      ?? record.activityInstanceWorkgroupId
      ?? null,
    criteria: Array.isArray(record.criteria) ? record.criteria : [],
    scheduling_rules: Array.isArray(record.scheduling_rules)
      ? record.scheduling_rules
      : (Array.isArray(record.schedulingRules) ? record.schedulingRules : []),
  }
}

const extractConfidenceDetails = (result) => {
  if (!result || typeof result !== 'object') return []

  const meta = result.meta && typeof result.meta === 'object' ? result.meta : {}
  const candidates = [
    result,
    result.scheduling_confidence_details,
    result.scheduling_confidence_scores,
    result.person_scheduling_confidence,
    result.person_scheduling_scores,
    result.confidence_details,
    result.scheduling_confidence,
    result.scheduled_people,
    meta.scheduling_confidence_details,
    meta.scheduling_confidence_scores,
    meta.person_scheduling_confidence,
    meta.person_scheduling_scores,
    meta.confidence_details,
    meta.scheduling_confidence,
    meta.scheduled_people,
    meta,
  ]

  const records = candidates.flatMap(asConfidenceRecords)
  if (!records.length && Array.isArray(result.summary_list)) {
    records.push(...result.summary_list.flatMap((item) => asConfidenceRecords(item)))
  }

  const seen = new Set()
  return records
    .map(normalizeConfidenceRecord)
    .filter((record) => {
      const key = `${record.person_id ?? 'unknown'}:${record.activity_instance_workgroup_id ?? ''}:${record.score ?? ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const formatConfidenceScore = (score) => {
  const number = Number(score)
  return Number.isFinite(number) ? `${Math.round(number)}%` : '—'
}

const personScoreClass = (score) => {
  const number = Number(score)
  if (!Number.isFinite(number)) return 'score-unknown'
  if (number >= 75) return 'score-high'
  if (number >= 50) return 'score-medium'
  return 'score-low'
}

const personConfidenceKey = (person) => (
  `${person.person_id ?? person.person_name}:${person.activity_instance_workgroup_id ?? ''}`
)

const openConfidenceDetails = (person) => {
  selectedConfidence.value = person
  showConfidenceDetails.value = true
}

onMounted(async () => {
  const id = route.params.id
  const type = route.meta?.summaryType || route.params.type || 'activity-instance'
  summaryType.value = type

  try {
    if (type === 'activity-instance') {
      const result = await store.dispatch('fetchActivityInstanceSummary', id)
      summaryList.value = result.summary_list || result
      confidenceScore.value = result.scheduling_confidence_score ?? null
      confidenceDetails.value = extractConfidenceDetails(result)
      title.value = 'Activity Instance Summary'
      subtitle.value = `Instance ID: ${id}`
    } else if (type === 'aiwg') {
      const result = await store.dispatch('fetchAIWGSummary', id)
      summaryList.value = result.summary_list || result
      confidenceDetails.value = extractConfidenceDetails(result)
      title.value = 'Instance Workgroup Summary'
      subtitle.value = `Instance Workgroup ID: ${id}`
      // Set signup-only metadata for the button
      if (result.meta) {
        isSignupOnly.value = result.meta.is_signup_only || false
        metaLocationId.value = result.meta.location_id
        metaAiwgId.value = result.meta.aiwg_id
      }
      confidenceScore.value = result.scheduling_confidence_score
        ?? result.meta?.scheduling_confidence_score
        ?? null
      const requestedPersonId = route.query.personid
      if (requestedPersonId !== undefined) {
        const requestedPerson = confidenceDetails.value.find(
          person => String(person.person_id) === String(requestedPersonId)
        )
        if (requestedPerson) openConfidenceDetails(requestedPerson)
      }
    }
  } catch (e) {
    error.value = e.message || 'Failed to load summary.'
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.summary-page-wrap { padding: 20px; max-width: 900px; margin: 0 auto; }
.sp-header { display: flex; align-items: center; gap: 16px; padding: 20px 24px; border-radius: 14px; margin-bottom: 24px; }
.sp-header-purple { background: linear-gradient(135deg, #7c3aed, #a78bfa); color: #fff; }
.sp-header-teal { background: linear-gradient(135deg, #0d9488, #5eead4); color: #fff; }
.sp-header-blue { background: linear-gradient(135deg, #4f46e5, #818cf8); color: #fff; }
.sp-header-icon { font-size: 40px; opacity: 0.9; }
.sp-header-title { margin: 0; font-size: 1.15rem; font-weight: 700; }
.sp-header-sub { margin: 2px 0 0; font-size: 0.82rem; opacity: 0.85; }
.confidence-summary { display: flex; align-items: center; gap: 8px; padding: 10px 14px; margin: -14px 0 20px; background: #ecfdf5; border: 1px solid #86efac; color: #166534; border-radius: 8px; font-size: 0.85rem; }
.confidence-summary strong { margin-left: auto; font-size: 1.05rem; }
.scheduled-people { margin-bottom: 24px; padding: 16px; border: 1px solid #c7d2fe; border-radius: 12px; background: #f8faff; }
.scheduled-people-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.scheduled-people-heading h6 { margin: 0; color: #312e81; font-size: 0.95rem; font-weight: 700; }
.scheduled-people-heading p { margin: 4px 0 0; color: #64748b; font-size: 0.78rem; }
.scheduled-people-heading > i { color: #4f46e5; font-size: 24px; }
.scheduled-person-list { display: grid; gap: 8px; }
.scheduled-person-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
.scheduled-person-identity { display: flex; align-items: center; gap: 10px; min-width: 0; }
.scheduled-person-identity > i { flex-shrink: 0; color: #6366f1; font-size: 22px; }
.scheduled-person-identity div { display: flex; flex-direction: column; min-width: 0; }
.scheduled-person-identity strong { color: #334155; font-size: 0.84rem; overflow-wrap: anywhere; }
.scheduled-person-identity span { color: #64748b; font-size: 0.73rem; }
.scheduled-person-actions { display: flex; align-items: center; flex-shrink: 0; gap: 10px; }
.scheduled-person-score { min-width: 42px; color: #334155; font-size: 0.82rem; font-weight: 700; text-align: right; }
.scheduled-person-score.score-high { color: #15803d; }
.scheduled-person-score.score-medium { color: #b45309; }
.scheduled-person-score.score-low { color: #b91c1c; }
.scheduled-person-score.score-unknown { color: #64748b; }

.sp-category { margin-bottom: 22px; }
.sp-cat-title { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; font-weight: 700; color: #4f46e5; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #e8eaf6; }
.sp-cat-title i { font-size: 20px; }

.sp-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 16px; background: #f8fafc; border-radius: 10px; margin-bottom: 8px; font-size: 0.85rem; color: #344767; line-height: 1.6; border-left: 3px solid #e2e8f0; transition: all 0.15s; }
.sp-item:hover { background: #f1f5f9; border-left-color: #4f46e5; }
.sp-item-warn { background: #fffbeb; border-left-color: #f59e0b; }
.sp-item-ok { background: #f0fdf4; border-left-color: #22c55e; }
.sp-item-err { background: #fef2f2; border-left-color: #ef4444; }
.sp-item-icon { font-size: 20px; color: #7b809a; margin-top: 1px; flex-shrink: 0; }
.sp-icon-warn { color: #f59e0b; }
.sp-icon-ok { color: #22c55e; }
.sp-icon-err { color: #ef4444; }
.sp-item-text :deep(strong) { color: #1e293b; font-weight: 700; }
.sp-item-text p { margin: 5px 0 0; }
.sp-category { scroll-margin-top: 72px; }

@media (max-width: 600px) {
  .scheduled-person-row { align-items: flex-start; flex-direction: column; }
  .scheduled-person-actions { align-self: stretch; justify-content: flex-end; }
}
</style>
